<template>
    <div>
        <h3 class="stack-m">Split Test</h3>
        <div v-if="!hasVersions && !isEditing" class="grid-content">
            <div class="grid-item">
                <p class="text-muted">Create two or more options and send each to your audience to see which works
                    best.</p>
            </div>
            <div class="grid-item no-flex">
                <r-button @click="isEditing = true">Create Split Test</r-button>
            </div>
        </div>
        <div v-else>
            <p class="text-muted">Weighted Distribution</p>
            <table class="table stack-m">
                <colgroup>
                    <col style="width: 100px">
                    <col style="max-width: 350px;width: 100%;">
                    <col style="width: 50px">
                </colgroup>
                <tbody>
                <tr
                    v-for="(version, versionIndex) in items"
                    :key="versionIndex">
                    <td>
                        <r-input
                            :validate="validate.$each.$iter[versionIndex].weight"
                            :value="version.weight"
                            @input="updateWeight(versionIndex, $event)"/>
                    </td>
                    <td>
                        <div>
                            <span class="no-flex no-wrap" v-if="!editableName">Version {{versionIndex + 1}}</span>
                            <r-button
                                v-else
                                data-tooltip="Edit version name"
                                class="has-tooltip no-flex no-wrap"
                                @click="editVersionName(versionIndex)"
                                type="link">{{version.name}}</r-button>
                        </div>
                        <item-weight-bar :relative-weight="version.relativeWeight"/>
                    </td>
                    <td>
                        <div class="display-flex align-items-center justify-content-space-between">
                            <v-icon-button
                                v-if="canDuplicate"
                                type="basic"
                                tooltip="Duplicate test"
                                @click="addDuplicate(versionIndex)">
                                <v-icon icon="clone"/>
                            </v-icon-button>
                            <v-icon-button
                                type="basic"
                                :disabled="isNotDeletable(versionIndex)"
                                @click="deleteVersion(versionIndex)">
                                <v-icon icon="trash"/>
                            </v-icon-button>
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>
            <div class="grid-content stack-s">
                <div class="grid-item no-flex">
                    <r-button type="link" @click="addVersion">Add Version</r-button>
                </div>
                <div class="grid-item no-flex" v-if="canSetDistribution">
                    <r-button type="link" @click="setWeightDistributionModal">Set Weight Distribution</r-button>
                </div>
            </div>
            <r-button v-if="hasVersions" fluid @click="showPickWinnerModal">Pick a Winner</r-button>
            <v-form-wizard-modal v-if="isPickWinnerModalVisible" @close="closePickWinnerModal" @submit="submitVersionWinner">
                <v-form-wizard-step step-name="Pick a winning version" step-key="select-winner">
                    <pick-winner-step
                        attribute-name="winner"
                        :use-name="editableName"
                        :versions="versions"/>
                </v-form-wizard-step>
                <v-form-wizard-step step-name="Confirm Selection" step-key="confirm-winner">
                    <confirm-winner-step
                        attribute-name="confirm"
                        :versions="versions"
                        :use-name="editableName"
                        previous-attribute-name="winner"/>
                </v-form-wizard-step>
            </v-form-wizard-modal>
            <v-modal
                v-if="isRenamingModalVisible"
                :scroll="true"
                title="Edit Version Name"
                cancelLabel="Close"
                @close="closeRenamingModal">
                <template #content>
                    <div class="grid-content">
                        <div class="grid-item grid-item-full" v-if="$v.$error">
                            <v-inline-notifications type="negative" :permanent="true">
                                <template #content>
                                    You must provide a name for the version
                                </template>
                            </v-inline-notifications>
                        </div>
                        <div class="grid-item grid-item-full">
                            <r-input
                                label="Name"
                                :validate="$v.versionName"
                                @key-submit="saveVersionName"
                                help-text="Enter a version name at least one character long."
                                v-model="versionName"/>
                        </div>
                    </div>
                </template>
                <template #actions>
                    <r-button
                        type="primary"
                        @click="saveVersionName">Apply
                    </r-button>
                </template>
            </v-modal>
            <v-modal
                title="Weight Distribution"
                v-if="isWeightDistributionModalVisible"
                @close="setWeightDistributionModal(false)"
            >
                <template #content>
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <p class="stack-m">Please select an option to distribute the weight on split test</p>
                            <v-radio class="stack-s" group="weightDistribution" :value="true" :model="true">
                                <template #label>
                                    <p>Use the set weight</p>
                                    <p class="text-muted">Rebilly will select according to the weighted randomness you set.</p>
                                </template>
                            </v-radio>
                            <v-radio class="stack-s" group="weightDistribution" value="b" disabled>
                                <template #label>
                                    <p>Use machine learning</p>
                                    <p class="text-muted">Rebilly will optimize and adjust the weights periodically using machine learning.</p>
                                </template>
                            </v-radio>
                            <v-radio class="stack-s" group="weightDistribution" value="c" disabled>
                                <template #label>
                                    <p>Use random weights daily</p>
                                    <p class="text-muted">Rebilly will randomize the weights daily. This means we'll have a scheduled job that randomly changes the weights daily.</p>
                                </template>
                            </v-radio>
                        </div>
                    </div>
                </template>
                <template #actions>
                    <r-button
                        type="primary"
                        @click="saveDistribution">Select
                    </r-button>
                </template>
            </v-modal>
        </div>
    </div>
</template>

<script>
    import {
        vIconButton, vIcon, vRadio,
    } from '@/components/form/index';
    import ItemWeightBar from '@/modules/rules-engine/actions/shared/item-weight-bar';
    import vFormWizardModal from '@/components/form-wizard/v-form-wizard-modal';
    import vFormWizardStep from '@/components/form-wizard/v-form-wizard-step';
    import vModal from '@/components/modal/v-modal';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import PickWinnerStep from './pick-winner-step';
    import ConfirmWinnerStep from './confirm-winner-step';
    import {required} from 'vuelidate/lib/validators';

    export default {
        name: 'split-test',
        components: {
            ItemWeightBar,
            vIcon,
            vIconButton,
            vRadio,
            vFormWizardModal,
            vFormWizardStep,
            vModal,
            vInlineNotifications,
            PickWinnerStep,
            ConfirmWinnerStep,
        },
        props: {
            isFormUpdating: Boolean,
            deleteAllowed: Boolean,
            /**
             * @type {EmailModel[]|*}
             */
            versions: Array,
            validate: Object,
            editableName: Boolean,
            canDuplicate: {
                type: Boolean,
                default: false,
            },
            canSetDistribution: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            hasVersions() {
                return this.versions.length > 1;
            },
            totalWeight() {
                return this.versions.reduce((total, {weight = 0} = {}) => {
                    total += isNaN(Number(weight)) ? 0 : Number(weight);
                    return total;
                }, 0);
            },
            items() {
                return this.versions.map(({weight, name = null}) => ({
                    weight,
                    relativeWeight: this.getRelativeWeight(weight),
                    name,
                }));
            },
        },
        data() {
            return {
                isEditing: false,
                newIndexes: [],
                isPickWinnerModalVisible: false,
                isRenamingModalVisible: false,
                isWeightDistributionModalVisible: false,
                editingIndex: null,
                versionName: null,
            };
        },
        validations: {
            versionName: {required},
        },
        methods: {
            updateWeight(index, weight) {
                this.$emit('update-weight', {index, weight});
            },
            getRelativeWeight(weight) {
                if (this.totalWeight === 0 || isNaN(weight)) {
                    return 0;
                }
                return Math.round((weight / this.totalWeight) * 100);
            },
            setWeightDistributionModal(value = true) {
                this.isWeightDistributionModalVisible = value;
            },
            saveDistribution() {
                this.setWeightDistributionModal(false);
            },
            addVersion() {
                this.$emit('add-version');
                this.newIndexes.push(this.items.length);
            },
            submitVersionWinner({confirm, winner}) {
                this.closePickWinnerModal();
                if (confirm) {
                    this.$emit('pick-version', winner);
                }
            },
            deleteVersion(index) {
                this.$emit('delete-version', index);
                this.newIndexes.splice(this.newIndexes.findIndex(v => v === index), 1);
            },
            isNotDeletable(index) {
                if (this.items.length === 1) {
                    return true;
                } if (!this.deleteAllowed) {
                    return this.newIndexes.find(v => v === index) === undefined;
                }
                return false;
            },
            addDuplicate(index) {
                this.$emit('add-version', {
                    version: this.versions[index],
                });
                this.newIndexes.push(this.items.length);
            },
            showPickWinnerModal() {
                this.isPickWinnerModalVisible = true;
            },
            closePickWinnerModal() {
                this.isPickWinnerModalVisible = false;
            },
            showRenamingModal(index) {
                this.editingIndex = index;
                this.versionName = this.versions[index].name;
                this.isRenamingModalVisible = true;
            },
            closeRenamingModal() {
                this.isRenamingModalVisible = false;
            },
            editVersionName(index) {
                this.showRenamingModal(index);
            },
            saveVersionName() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.$emit('update-name', {
                        index: this.editingIndex,
                        name: this.versionName,
                    });
                    this.closeRenamingModal();
                }
            },
        },
    };
</script>
